import {useCallback, useState} from 'react';
import {isEffectivelyOffline} from 'src/offline/connectivity';
import {ALREADY_SYNCING_MESSAGE, syncOutbox} from 'src/offline/syncManager';

export interface InspectionSyncControls {
  sync: () => Promise<void>;
  isSyncing: boolean;
  lastError: string | undefined;
}

// Manual sync trigger for a session — the central scheduler owns automatic
// reconnect/interval draining, so this only exposes the user-initiated push.
export const useInspectionSync = (
  sessionId: string | undefined
): InspectionSyncControls => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastError, setLastError] = useState<string | undefined>();

  const sync = useCallback(async () => {
    if (!sessionId || isEffectivelyOffline()) return;
    setIsSyncing(true);
    setLastError(undefined);
    try {
      await syncOutbox(sessionId);
    } catch (error) {
      const message = (error as Error).message;
      // A concurrent scheduler drain is a benign skip, not a user-facing error.
      if (message !== ALREADY_SYNCING_MESSAGE) setLastError(message);
    } finally {
      setIsSyncing(false);
    }
  }, [sessionId]);

  return {sync, isSyncing, lastError};
};
